import * as d3 from 'd3-format'
import moment from 'moment'
import localStore from 'store'
import english from 'locales/en-US'
import german from 'locales/de-DE'
import * as deEU from '../locales/format/d3_de_EU.json'
import { ordinalMap, currencies } from './enums'

const translate = (id) => {
  const language = localStore.get('app.settings.locale')
  const message = language === 'de-DE' ? german.messages[id] : english.messages[id]
  return message
}

// Locale & Currency
d3.formatDefaultLocale(deEU.default)
export function setCurrency(code) {
  const c = currencies
  if (code)
    d3.formatDefaultLocale({
      ...deEU.default,
      currency: ['', ` ${c[code] ? c[code].symbol : c.EUR.symbol}`],
    })
}

export const getCurrency = () => {
  // Extract the symbol from D3
  return d3
    .format('$')('1')
    .replace(/[\d\s]/g, '')
}

// Formatting (money, skalar, percent, dates)

const MIN_VALUE = 0.0001 // avoid rounding issues.
export const isNotValue = (value) => (!value && value !== 0) || Number.isNaN(Number(value))
const isBelowMinValue = (v) =>
  (Math.abs(v) > 0 && Math.abs(v) < MIN_VALUE) || (Math.abs(v) < 0 && Math.abs(v) > -MIN_VALUE)

export const getFormatter = (value, formatter) => {
  if (typeof formatter === 'function') return formatter(value)
  switch (formatter) {
    case 'percent':
      return percent(value) // last element is the text
    case 'money':
      return money(value)
    case 'shares':
      return skalar(value)
    case 'skalar':
      return skalar(value)
    case 'moneyExact':
    case 'price':
      return moneyExact(value)
    case 'OrdinalFormatter':
      return OrdinalFormatter(value)
    case 'date':
      return showDate(value)
    default:
      return value
  }
}

// Money

// 1,00k €
export const money = (v, NA = 'N/A', currency) => {
  if (isNotValue(v)) return NA
  if (currency) setCurrency(currency)
  if (isBelowMinValue(v)) return money(0)
  return (v > 1 || v < -1 ? d3.format('$,.3s')(v) : d3.format('$,.3')(v)).replace(
    'G',
    translate('d3.format.giga'),
  )
}
// 1,00k
export const moneyNoCurrency = d3.format(',.3s')

// 1.000,00 €
export const moneyExact = (v, NA = 'N/A', hideCurrency = false) => {
  if (isNotValue(v)) return NA
  if (isBelowMinValue(v)) return moneyExact(0)
  const c = hideCurrency ? '' : '$'
  return d3.format(`${c},.2f`)(v)
}

// Skalar
export const skalar = (v, NA = 'N/A') => (isNotValue(v) ? NA : d3.format(',.0f')(v))
export const skalarDecimal = (decimalPoints) => d3.format(`,.${decimalPoints}f`)

// Percent
export const percent = (v, NA = 'N/A') => (isNotValue(v) ? NA : d3.format('.2%')(v))

// Dates
export const date = (isoString) =>
  isoString && !moment.isMoment(isoString) ? moment(isoString) : isoString

export const showDateShort = (d) => formatDate(d, 'MMM YY')
export const showDate = (d) => formatDate(d, 'DD.MM.YYYY')
export const showDateDetail = (d) => formatDate(d, 'dddd, MMMM Do YYYY, h:mm:ss a')
export const showDateToday = (d, translate, detail = false) =>
  moment(d).calendar(null, {
    sameDay: () =>
      `[${translate('date.today')},${detail ? formatDate(d, ' MMMM Do YYYY') : ''}] h:mm:ss a`,
    lastDay: () =>
      `[${translate('date.yesterday')},${detail ? formatDate(d, ' MMMM Do YYYY') : ''}] h:mm:ss a`,
    sameElse: () => (detail ? showDateDetail(d) : showDate(d)),
  })

// Small helper function
const formatDate = (dateFunc, format) => {
  return dateFunc && dateFunc._isAMomentObject
    ? dateFunc.format(format)
    : moment(dateFunc).format(format)
}

export function OrdinalFormatter(number) {
  let selected = 'th'
  const str = `${number}`
  const last = str[str.length - 1]
  const before = str.length > 1 ? str[str.length - 2] : null

  // 11 -> eleventh : th not st
  if (ordinalMap[last] && last !== before && before !== '1') {
    selected = ordinalMap[number]
  }

  return `${skalar(number)} ${selected}`
}

export const textToCsv = (csv) => {
  const allTextLines = csv.split(/\r\n|\n/)
  const lines = []
  for (let i = 0; i < allTextLines.length; i++) {
    const data = allTextLines[i].split(';')
    const tarr = []
    for (let j = 0; j < data.length; j++) {
      tarr.push(data[j])
    }
    lines.push(tarr)
  }
  return lines
}

export const roundShares = (value, project) => {
  switch (project.roundingMode) {
    case 'CEILING':
      return Math.ceil(value)
    case 'HALF_UP':
      return Math.round(value)
    case 'FLOOR':
    default:
      return Math.floor(value)
  }
}
